/**
 * Shared display helpers for the two-file Baseline Alignment comparison
 * (Category 7 — Checks #31–35).
 *
 * BASELINE-SUB4 (render). The engine (Sub-1) and upload route (Sub-2a) already
 * computed and persisted the comparison; this module is the SINGLE source that
 * shapes those canonical fields for display, so the PDF assembler and the
 * dashboard components render the same numbers and cannot drift (Permanent
 * Rule 12; Amendment §A2 — display surfaces format, never recompute). It
 * mirrors the pattern of {@link module:shared/check36Display}.
 *
 * Canonical inputs (all confirmed present on the frozen engine @ 798bd2f):
 *   - analysis.comparison = { isComparison, baselineFilename, baselineDataDate }
 *     (set by the upload route; rides in analysis.json + the dashboard payload).
 *   - Category 7 is VISIBLE and score-neutral when a baseline is attached
 *     (all five checks REPORT_ONLY → categoryScore 0, effectiveWeight 0).
 *   - Per-check aux (spread onto the check): #31 added_count, #32 deleted_count,
 *     #33 numerator, #34 numerator, #35 variance_days / variance_direction /
 *     variance_threshold_days / current_finish / baseline_finish.
 *   - severity_findings[] shapes: activity rows (#31/#32/#33 — taskCode,
 *     taskName, wbsPath, durationDays, details), a relationship row (#34 —
 *     details.{predecessorCode, successorCode, oldType, newType, oldLag,
 *     newLag}), a project-finish row (#35 — details.{varianceDays, direction,
 *     currentFinish, baselineFinish}).
 *
 * @module shared/baselineComparisonDisplay
 */

import { formatDateDDMonYYYY } from '../utils/dates.js';
import { ACTIVITY_PREVIEW_ROWS, smartPreviewCap } from './activityPreview.js';

/** The five checks that make up the Baseline Alignment comparison (Category 7). */
export const BASELINE_COMPARISON_CHECKS = new Set([31, 32, 33, 34, 35]);

/** MEGAC-FIX F3 (supersedes the G-4 cap of 50): the #31/#32/#33 comparison
 *  tables share the standard-check inline preview contract — the same
 *  ACTIVITY_PREVIEW_ROWS cap with smart-show, an explicit "Showing N of M"
 *  note, and the full list in the per-check XLSX attachment (#31–34 are
 *  exportable as of F3). One cap for every inline table, both surfaces. */
export const COMPARISON_TABLE_CAP = ACTIVITY_PREVIEW_ROWS;

/**
 * Trim "CW_TEST > Substructure > Foundations" → "Foundations".
 * The comparison activity tables (and the v6 activity table) render the leaf WBS
 * segment only; the full breadcrumb lives in the attached XLSX. Lifted here from
 * the PDF assembler so both surfaces share one implementation.
 *
 * @param {string|null|undefined} path
 * @returns {string}
 */
export function simplifyWbsPath(path) {
  if (path === null || path === undefined) return '';
  const parts = String(path).split('>').map((s) => s.trim()).filter(Boolean);
  return parts.length === 0 ? '' : parts[parts.length - 1];
}

/** Find a check by its canonical number within a category's checks array. */
function findCheck(checks, n) {
  return (checks || []).find((c) => c && c.check_number === n) || null;
}

/**
 * Read the six comparison figures from a Category 7 object (verbatim canonical
 * fields — never recomputed). Tolerates any missing check → 0 / null so callers
 * never throw on a degenerate/data-quality category.
 *
 * @param {object|null} cat7 - the Category 7 result object (has .checks)
 */
function readComparisonNumbers(cat7) {
  const checks = cat7?.checks || [];
  const c31 = findCheck(checks, 31);
  const c32 = findCheck(checks, 32);
  const c33 = findCheck(checks, 33);
  const c34 = findCheck(checks, 34);
  const c35 = findCheck(checks, 35);
  return {
    added: c31?.added_count ?? 0,
    deleted: c32?.deleted_count ?? 0,
    durationChanges: c33?.numerator ?? 0,
    changeThresholdPct: c33?.change_threshold_pct ?? null,
    logicChanges: c34?.numerator ?? 0,
    varianceDays: c35?.variance_days ?? null,
    varianceDirection: c35?.variance_direction ?? null,
    varianceThresholdDays: c35?.variance_threshold_days ?? null,
  };
}

/**
 * The cover-callout / banner view-model. Returns null unless the analysis is a
 * comparison. Reads Category 7 by id then its checks by check_number.
 *
 * @param {object} analysis - analyzeSchedule output (or dashboard payload)
 * @returns {null | {
 *   baselineFilename: string|null,
 *   baselineDataDate: string|null,
 *   varianceDays: number|null,
 *   varianceDirection: string|null,
 *   varianceThresholdDays: number|null,
 *   added: number, deleted: number,
 *   durationChanges: number, logicChanges: number,
 * }}
 */
export function getComparisonSummary(analysis) {
  if (!analysis || !analysis.comparison || !analysis.comparison.isComparison) return null;
  const cat7 = (analysis.categories || []).find((c) => c && c.id === 7) || null;
  const nums = readComparisonNumbers(cat7);
  return {
    baselineFilename: analysis.comparison.baselineFilename ?? null,
    baselineDataDate: analysis.comparison.baselineDataDate ?? null,
    ...nums,
  };
}

/**
 * True for a VISIBLE, score-neutral category — i.e. Baseline Alignment when a
 * baseline is attached (all five checks REPORT_ONLY). The engine emits
 * `categoryScore: 0` + `effectiveWeight: 0` for this today (NOT a null score),
 * so `effectiveWeight === 0` is the canonical signal (locked by
 * baselineComparison.progressed.test.js). A future null score is tolerated too.
 * Display surfaces use this to render a neutral "COMPARISON" marker instead of a
 * coloured "0" score / score bar / severity badge.
 *
 * @param {object} category
 * @returns {boolean}
 */
export function isScoreNeutralCategory(category) {
  if (!category || category.hidden) return false;
  return category.effectiveWeight === 0 || category.score === null || category.score === undefined;
}

/**
 * The finish-variance headline for the cover callout / dashboard banner —
 * single builder for both surfaces (Rule 12). Magnitude + direction word,
 * never a signed number, and never an "On baseline" claim when the variance
 * was not computed (Data Integrity on the data-quality path).
 *
 * @param {object|null} summary - getComparisonSummary() output (or compatible)
 * @returns {{ text: string, alert: boolean }} alert = delayed beyond threshold
 *   (an attention cue, not a severity)
 */
export function buildVarianceHeadline(summary) {
  const vd = summary?.varianceDays;
  if (vd === null || vd === undefined) {
    return { text: 'Finish variance unavailable', alert: false };
  }
  const direction = summary?.varianceDirection;
  const abs = Math.abs(vd);
  const unit = abs === 1 ? 'day' : 'days';
  let text;
  if (direction === 'delayed') text = `${abs} ${unit} behind baseline`;
  else if (direction === 'ahead') text = `${abs} ${unit} ahead of baseline`;
  else text = 'On baseline';
  const alert = direction === 'delayed'
    && typeof vd === 'number'
    && typeof summary.varianceThresholdDays === 'number'
    && vd > summary.varianceThresholdDays;
  return { text, alert };
}

/**
 * The four scope figures for the cover callout / dashboard banner scope row.
 * Added/Deleted stay participles (read fine at 1); change counts pluralise.
 *
 * @param {object|null} summary - getComparisonSummary() output (or compatible)
 * @returns {Array<{ value: number, label: string }>}
 */
export function buildScopeItems(summary) {
  const added = summary?.added ?? 0;
  const deleted = summary?.deleted ?? 0;
  const durationChanges = summary?.durationChanges ?? 0;
  const logicChanges = summary?.logicChanges ?? 0;
  return [
    { value: added, label: 'Added' },
    { value: deleted, label: 'Deleted' },
    { value: durationChanges, label: `Duration ${plural(durationChanges, 'change')}` },
    { value: logicChanges, label: `Logic ${plural(logicChanges, 'change')}` },
  ];
}

/**
 * One-line summary for the visible comparison category — the SINGLE summariser
 * for both the dashboard CategoryRow and the PDF category-scores row (Rule 12).
 * Magnitude + direction word for the finish (a signed "+45d"/"-10d" breaks on
 * ahead schedules — direction-word form supersedes the Spec v2 §17 signed
 * example, reviewer-approved deviation).
 *
 * e.g. "2 added, 1 deleted, 1 duration change (>20%), 1 logic change,
 *       finish 45 days delayed"
 *
 * @param {object} category - the Category 7 result object
 * @returns {string}
 */
export function buildComparisonCategorySummary(category) {
  const n = readComparisonNumbers(category);
  const threshold = n.changeThresholdPct === null || n.changeThresholdPct === undefined
    ? ''
    : ` (>${n.changeThresholdPct}%)`;
  let finish;
  if (n.varianceDays === null || n.varianceDays === undefined) {
    finish = 'finish variance n/a';
  } else if (n.varianceDirection === 'delayed' || n.varianceDirection === 'ahead') {
    const abs = Math.abs(n.varianceDays);
    finish = `finish ${abs} ${abs === 1 ? 'day' : 'days'} ${humaniseDirection(n.varianceDirection)}`;
  } else {
    finish = 'finish on baseline';
  }
  return `${n.added} added, ${n.deleted} deleted, `
    + `${n.durationChanges} duration ${plural(n.durationChanges, 'change')}${threshold}, `
    + `${n.logicChanges} logic ${plural(n.logicChanges, 'change')}, ${finish}`;
}

/**
 * MEGAC-FIX-C2 P3: the Category-Scores weight footnote, rendered from the same
 * emitted baseline-mode state the page header uses (the Baseline Alignment
 * category's `hidden` flag — Rule 12, single source). The single (no-baseline)
 * exhibit was contradicting itself: the header parenthetical said "Baseline
 * Alignment hidden — no baseline detected" while the footnote claimed
 * "informational in comparison mode". Both surfaces now key on one signal.
 *
 *   comparison mode (Baseline Alignment visible)  →
 *     "Baseline (5%) is informational in comparison mode — weight redistributed proportionally."
 *   no-baseline mode (Baseline Alignment hidden)  →
 *     "Baseline Alignment (5%) hidden — no baseline detected; weight redistributed proportionally."
 *
 * @param {object} analysis - analyzeSchedule output (reads analysis.categories)
 * @returns {string}
 */
export function buildWeightRedistributionFootnote(analysis) {
  const cats = (analysis && analysis.categories) || [];
  const cat7 = cats.find((c) => c && c.id === 7);
  // No cat7 at all (defensive) reads as no-baseline. Baseline Alignment hidden
  // ⟺ no baseline attached ⟺ not comparison mode (the header's own signal).
  const baselineHidden = cat7 ? !!cat7.hidden : true;
  return baselineHidden
    ? 'Baseline Alignment (5%) hidden — no baseline detected; weight redistributed proportionally.'
    : 'Baseline (5%) is informational in comparison mode — weight redistributed proportionally.';
}

/** Pluralise a noun on a count: (1,'change') → 'change'; (2,'change') → 'changes'. */
function plural(count, singular) {
  return count === 1 ? singular : `${singular}s`;
}

/** Humanise a variance-direction enum for display ('on_track' → 'on track'). */
function humaniseDirection(direction) {
  return String(direction ?? '').replace(/_/g, ' ');
}

/**
 * The header result phrase for a comparison check, or null for any check outside
 * the baseline set. Every value traces to one canonical field.
 *
 * @param {object} check - canonical CheckResult
 * @returns {string|null}
 */
export function buildComparisonResultPhrase(check) {
  if (!check || !BASELINE_COMPARISON_CHECKS.has(check.check_number)) return null;
  switch (check.check_number) {
    case 31:
      return `${check.added_count ?? 0} added`;
    case 32:
      return `${check.deleted_count ?? 0} deleted`;
    case 33: {
      const n = check.numerator ?? 0;
      return `${n} duration ${plural(n, 'change')}`;
    }
    case 34: {
      const n = check.numerator ?? 0;
      return `${n} logic ${plural(n, 'change')}`;
    }
    case 35: {
      if (check.variance_days === null || check.variance_days === undefined) {
        return 'finish variance n/a';
      }
      // The engine emits a SIGNED variance_days ('ahead' ⇒ negative). Render the
      // magnitude + the direction word (matching the cover callout / banner),
      // never a negative number beside "ahead".
      const days = Math.abs(check.variance_days);
      return `${days} ${days === 1 ? 'day' : 'days'} · ${humaniseDirection(check.variance_direction)}`;
    }
    default:
      return null;
  }
}

/** "PR_FS" → "FS" (strip the P6 relationship-type prefix for display). */
function shortRelType(t) {
  return String(t ?? '').replace(/^PR_/, '');
}

/**
 * Build the #34 relationship line from a finding's details.
 *   "A04 → A05" (+ " · type FS→SS" when the type changed)
 *              (+ " · lag 0h → 50h" when the lag changed).
 * Lag stays in HOURS with an `h` suffix — the finding carries hours; no template
 * division (§A2).
 */
function buildRelationshipLine(details) {
  const d = details || {};
  const pred = d.predecessorCode ?? '';
  const succ = d.successorCode ?? '';
  let text = `${pred} → ${succ}`;
  if (d.oldType !== undefined && d.newType !== undefined && d.oldType !== d.newType) {
    text += ` · type ${shortRelType(d.oldType)}→${shortRelType(d.newType)}`;
  }
  if (d.oldLag !== undefined && d.newLag !== undefined && d.oldLag !== d.newLag) {
    text += ` · lag ${d.oldLag}h → ${d.newLag}h`;
  }
  return text;
}

/**
 * Build the normalised, serialization-safe body a template renders by `kind`.
 * Returns null for any check outside the baseline set.
 *
 *   31, 32 → { kind:'activityTable', columns, rows }  (ID/Activity/WBS/Duration)
 *   33     → { kind:'activityTable', columns, rows }  (ID/Activity/Duration/Change)
 *   34     → { kind:'relationshipLine', text }
 *   35     → { kind:'statement', text, baselineFinishLabel, currentFinishLabel }
 *
 * 31/32/33 with ZERO findings → { kind:'emptyNote', text:'None.' } — a single
 * muted line on both surfaces instead of a header-only table. The wording
 * lives here so the PDF and dashboard cannot diverge.
 *
 * @param {object} check - canonical CheckResult
 */
export function buildComparisonBody(check) {
  if (!check || !BASELINE_COMPARISON_CHECKS.has(check.check_number)) return null;
  const cn = check.check_number;
  const findings = check.severity_findings || [];
  // G-4 (Batch-RENDER-CAPS) + MEGAC-FIX F3: comparison tables are CAPPED at
  // the shared standard-check preview cap (smart-show) with an explicit
  // "Showing N of M" note — the full list lives in the per-check XLSX
  // attachment. The TRUE total survives the projection via
  // severity_findings_total.
  const totalCount = Number.isFinite(check.severity_findings_total)
    ? check.severity_findings_total
    : findings.length;
  const { cap } = smartPreviewCap(totalCount, COMPARISON_TABLE_CAP);
  const capped = findings.slice(0, cap);
  const truncated = totalCount > capped.length;
  const tableMeta = {
    totalCount,
    truncated,
    truncationNote: truncated ? `Showing ${capped.length} of ${totalCount}` : null,
  };

  if ((cn === 31 || cn === 32 || cn === 33) && findings.length === 0) {
    return { kind: 'emptyNote', text: 'None.' };
  }

  if (cn === 31 || cn === 32) {
    return {
      kind: 'activityTable',
      columns: [
        { key: 'taskCode', label: 'ID' },
        { key: 'taskName', label: 'Activity' },
        { key: 'wbsLeaf', label: 'WBS' },
        { key: 'durationLabel', label: 'Duration', align: 'right' },
      ],
      rows: capped.map((f) => ({
        taskCode: f.taskCode ?? '',
        taskName: f.taskName ?? '',
        wbsLeaf: simplifyWbsPath(f.wbsPath),
        durationLabel: `${f.durationDays ?? 0}d`,
      })),
      ...tableMeta,
    };
  }

  if (cn === 33) {
    return {
      kind: 'activityTable',
      columns: [
        { key: 'taskCode', label: 'ID' },
        { key: 'taskName', label: 'Activity' },
        { key: 'durationLabel', label: 'Duration', align: 'right' },
        { key: 'changeLabel', label: 'Change', align: 'right' },
      ],
      // durationDays here is the CURRENT planned duration (engine-confirmed);
      // changePct is the signed percentage delta from baseline.
      rows: capped.map((f) => {
        const pct = f.details?.changePct ?? 0;
        return {
          taskCode: f.taskCode ?? '',
          taskName: f.taskName ?? '',
          durationLabel: `${f.durationDays ?? 0}d`,
          changeLabel: `${pct > 0 ? '+' : ''}${pct}%`,
        };
      }),
      ...tableMeta,
    };
  }

  if (cn === 34) {
    // Render EVERY logic change (numerator may be > 1) — one line per finding —
    // so the body never under-reports the header count (Data Integrity). An
    // empty list (zero logic changes) renders nothing, not a stray "→".
    return {
      kind: 'relationshipLines',
      lines: findings.map((f) => buildRelationshipLine(f.details)),
    };
  }

  // cn === 35
  // MEGAC-FIX F12: the old statement ("Project finish N days delayed vs
  // baseline.") echoed the #35 explanation rendered directly above it. The
  // statement body is now the finish-date PAIR — the one figure the
  // explanation does NOT carry — read from the engine's canonical pair values
  // (check-level current_finish / baseline_finish, which survive the dashboard
  // projection as ISO strings since the capObject Date fix; the finding's
  // details remain the fallback). Never recomputed.
  const variance = check.variance_days;
  const f = findings[0];
  const baselineFinish = check.baseline_finish ?? f?.details?.baselineFinish ?? null;
  const currentFinish = check.current_finish ?? f?.details?.currentFinish ?? null;
  let text;
  if (baselineFinish && currentFinish) {
    text = `Baseline finish ${formatDateDDMonYYYY(baselineFinish)} → current finish ${formatDateDDMonYYYY(currentFinish)}`;
  } else if (variance === null || variance === undefined) {
    text = 'Project finish variance could not be computed against the baseline.';
  } else {
    const absDays = Math.abs(variance);
    text = `Project finish ${absDays} ${absDays === 1 ? 'day' : 'days'} ${humaniseDirection(check.variance_direction)} vs baseline.`;
  }
  return {
    kind: 'statement',
    text,
    // F12: the separate "Baseline … → Current …" sub-line is gone — the pair
    // IS the statement now. Keys retained (null) for template compatibility.
    baselineFinishLabel: null,
    currentFinishLabel: null,
  };
}
